import { Icon, Icons } from '@/components/icon';
import type { CallTag } from '@/hooks/calls/use-ongoing-call-tags';
import { useNetworkQuality } from '@/hooks/use-network-quality';
import { useTranslation } from '@/hooks/use-translation';
import { formatPhoneNumberForDisplay } from '@/lib/phone-number';
import { isDesktop } from '@/lib/platform';
import { cn } from '@/lib/utils';
import type { DeclineState } from '@/providers/app-call-actions/app-call-actions-provider';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import type {
  VoiceCallStateStatus,
  VoiceContact,
  VoiceContactExtra,
  VoiceContactUserLine,
} from '@packages/client-sdk-react/src/voice/voice-call-container';
import dayjs from 'dayjs';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { NetworkStatusIndicator } from '../dashboard/dialer/network-status-indicator';
import { ContactAvatar } from '../inbox/contact-avatar';
import { AutoSizeText } from '../ui/auto-size-text';
import { CountryFlag } from '../ui/country-flag';
import { LiveWaveform } from '../ui/live-waveform';
import { LoadingDots, LoadingSpinner } from '../ui/spinner';
import { P3 } from '../ui/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { CallTagsDropdown } from './call-tags-dropdown';
import { ContactSubline } from './contact-subline';

interface CallPipProps {
  callId?: string;
  duration: number;
  contact?: VoiceContact;
  callStatus: VoiceCallStateStatus;
  isMuted: boolean;
  direction: 'incoming' | 'outgoing' | 'unknown';
  onToggleMute: () => void;
  onAnswer: () => Promise<boolean>;
  onHangup: () => void;
  onDecline: () => void;
  showDeclinedState?: boolean;
  declineState?: DeclineState;
  onDismiss?: () => void;
  pipWindow?: Window;
  userLine?: VoiceContactUserLine;
  extra?: VoiceContactExtra;
  microphoneDeviceId?: string;
  className?: string;
  onContactClick?: () => void;
  callTags?: {
    tags: CallTag[];
    selectedTagKeys: string[];
    onAddTag: (key: string) => void;
    onRemoveTag: (key: string) => void;
  };
}

const MIN_PIP_WIDTH = 240;
const MIN_PIP_HEIGHT = 52;

const CALL_TAGS_MIN_PIP_WIDTH = 280;
// Keep in sync with the default heights used when the PiP is created:
// browser → use-call-picture-in-picture.ts (320), Electron → pip-window.ts (320).
const CALL_TAGS_MIN_PIP_HEIGHT_BROWSER = 320 + 204;
const CALL_TAGS_MIN_PIP_HEIGHT_ELECTRON = 320 + 174;

export function CallPip({
  callId,
  contact,
  callStatus,
  isMuted,
  duration,
  direction,
  onToggleMute,
  onAnswer,
  onDecline,
  onHangup,
  showDeclinedState = false,
  declineState,
  onDismiss,
  pipWindow,
  userLine,
  extra,
  microphoneDeviceId,
  className = '',
  onContactClick,
  callTags,
}: CallPipProps) {
  const { t } = useTranslation('desktop_ui');
  const isRinging = callStatus === 'ringing';
  const isIncoming = direction === 'incoming';
  const [isProcessing, setIsProcessing] = useState(false);
  const [isHangingUp, setIsHangingUp] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const [dropdownContainer, setDropdownContainer] = useState<HTMLDivElement | null>(null);
  const callIdentity =
    callId ??
    `${direction}:${contact?.phoneNumber ?? ''}:${contact?.name ?? ''}:${contact?.companyName ?? ''}`;
  const previousCallIdentityRef = useRef(callIdentity);

  const handleAnswer = async () => {
    if (isProcessing || showDeclinedState) {
      return;
    }
    setIsProcessing(true);
    let success = false;
    try {
      success = await onAnswer();
    } finally {
      if (!success) {
        setIsProcessing(false);
      }
    }
  };

  const handleDecline = () => {
    if (isProcessing || showDeclinedState) {
      return;
    }
    setIsProcessing(true);
    onDecline();
  };

  useEffect(() => {
    if (callStatus === 'idle' || callStatus === 'in-progress') {
      setIsHangingUp(false);
    }
  }, [callStatus]);

  useEffect(() => {
    if (previousCallIdentityRef.current === callIdentity) {
      return;
    }
    previousCallIdentityRef.current = callIdentity;
    setIsHangingUp(false);
    setIsProcessing(false);
  }, [callIdentity]);

  // Track the LiveWaveform's mic stream so we can stop it synchronously on hangup.
  // When hanging up from a Document PiP window, the PiP document may be destroyed
  // before React runs cleanup effects, orphaning the getUserMedia stream.
  const waveformStreamRef = useRef<MediaStream | null>(null);

  // Stable callbacks — inline functions would re-run LiveWaveform's setup effect
  // on every parent render (duration ticks every second), causing repeated
  // getUserMedia calls that can orphan streams via the async setup race
  const handleWaveformStreamReady = useCallback((stream: MediaStream) => {
    waveformStreamRef.current = stream;
  }, []);

  const handleWaveformStreamEnd = useCallback(() => {
    waveformStreamRef.current = null;
  }, []);

  const handleHangup = () => {
    if (isHangingUp) {
      return;
    }
    setIsHangingUp(true);

    // Stop the waveform mic stream immediately before the async hangup flow
    // closes the PiP window and potentially orphans the stream
    if (waveformStreamRef.current) {
      for (const track of waveformStreamRef.current.getTracks()) {
        track.stop();
      }
      waveformStreamRef.current = null;
    }

    onHangup();
  };

  // Use pipWindow if provided, fallback to global window
  const targetWindow = pipWindow || window;

  const { isOnline, isPoorQuality } = useNetworkQuality();

  // Track window dimensions for responsive layout
  const [windowDimensions, setWindowDimensions] = useState({
    width: targetWindow.innerWidth,
    height: targetWindow.innerHeight,
  });

  const userLineCountry = userLine?.lineNumber
    ? parsePhoneNumberFromString(userLine?.lineNumber)?.country
    : undefined;

  const handleContactClick = useCallback(() => {
    analytics.track(ANALYTICS_EVENTS.CALL.ACTIVE_CONTEXT_BUTTON_TAPPED);
    onContactClick?.();
  }, [onContactClick]);

  const [isCallTagsOpen, setIsCallTagsOpen] = useState(false);

  const handleEnsureCallTagsSize = useCallback(() => {
    if (pipWindow) {
      if (
        pipWindow.innerWidth < CALL_TAGS_MIN_PIP_WIDTH ||
        pipWindow.innerHeight < CALL_TAGS_MIN_PIP_HEIGHT_BROWSER
      ) {
        try {
          pipWindow.resizeTo(
            Math.max(CALL_TAGS_MIN_PIP_WIDTH, pipWindow.innerWidth),
            Math.max(CALL_TAGS_MIN_PIP_HEIGHT_BROWSER, pipWindow.innerHeight),
          );
        } catch {
          // Some browsers may reject resizeTo on Document PiP; ignore.
        }
      }
      return;
    }

    if (isDesktop() && window.pipCallWindowAPI?.resize) {
      if (
        window.innerWidth < CALL_TAGS_MIN_PIP_WIDTH ||
        window.innerHeight < CALL_TAGS_MIN_PIP_HEIGHT_ELECTRON
      ) {
        window.pipCallWindowAPI.resize(
          Math.max(CALL_TAGS_MIN_PIP_WIDTH, window.innerWidth),
          Math.max(CALL_TAGS_MIN_PIP_HEIGHT_ELECTRON, window.innerHeight),
        );
      }
    }
  }, [pipWindow]);

  const handleCallTagsOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        handleEnsureCallTagsSize();
      }
      setIsCallTagsOpen(next);
    },
    [handleEnsureCallTagsSize],
  );

  const handleOpenParentWindow = () => {
    if (isDesktop()) {
      // Electron: focus main window
      window.pipCallWindowAPI?.focusMain();
    } else if (pipWindow?.opener && !pipWindow.opener.closed) {
      // Web: focus opener
      pipWindow.opener.focus();
    }
  };

  const renderDeclineState = useCallback((): string => {
    switch (declineState?.state) {
      case 'ai-receptionist':
        return t('components.incomingCall.sentToAIReceptionist');
      case 'call-transfer':
        return t('components.incomingCall.sentToCallTransfer', {
          number: formatPhoneNumberForDisplay(declineState.extra, userLineCountry),
        });
      case 'voicemail':
        return t('components.incomingCall.sentToVoicemail');
      case 'cascade':
        return t('components.incomingCall.sentToCascade');
      default:
        return t('components.incomingCall.callDeclined');
    }
  }, [declineState, userLineCountry, t]);

  const declineTooltipText = useCallback((): string => {
    switch (declineState?.state) {
      case 'ai-receptionist':
        return t('components.incomingCall.declineToAIReceptionist');
      case 'call-transfer':
        return t('components.incomingCall.declineToCallTransfer', {
          number: formatPhoneNumberForDisplay(declineState.extra, userLineCountry),
        });
      case 'voicemail':
        return t('components.incomingCall.declineToVoicemail');
      case 'cascade':
        return t('components.incomingCall.declineToCascade');
      default:
        return t('components.incomingCall.declineCall');
    }
  }, [declineState, userLineCountry, t]);

  const renderCallStatus = useCallback(() => {
    switch (callStatus) {
      case 'initiating':
        return <P3 className="text-primary-60">{t('state.waiting', { ns: 'desktop_calls' })}</P3>;
      case 'ringing':
        return <P3 className="text-primary-60">{t('state.ringing', { ns: 'desktop_calls' })}</P3>;
      case 'in-progress':
        return <P3 className="tabular-nums">{dayjs.duration(duration * 1000).format('mm:ss')}</P3>;
      case 'transferring':
        return (
          <P3 className="text-primary-60">{t('state.callTransferred', { ns: 'desktop_calls' })}</P3>
        );
      default:
        return <LoadingDots className="text-primary/60" />;
    }
  }, [callStatus, duration, t]);

  useEffect(() => {
    const updateDimensions = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      const width = Math.round(rect?.width || targetWindow.innerWidth);
      const height = Math.round(rect?.height || targetWindow.innerHeight);

      setWindowDimensions((current) =>
        current.width === width && current.height === height ? current : { width, height },
      );
    };

    const ResizeObserverCtor =
      (targetWindow as Window & { ResizeObserver?: typeof ResizeObserver }).ResizeObserver ??
      (typeof ResizeObserver !== 'undefined' ? ResizeObserver : undefined);
    const observer =
      ResizeObserverCtor && rootRef.current ? new ResizeObserverCtor(updateDimensions) : null;
    if (rootRef.current) {
      observer?.observe(rootRef.current);
    }
    targetWindow.addEventListener('resize', updateDimensions);
    updateDimensions();

    return () => {
      observer?.disconnect();
      targetWindow.removeEventListener('resize', updateDimensions);
    };
  }, [targetWindow, isRinging, isIncoming, showDeclinedState]);

  const DESKTOP_HEADER_BUFFER = isDesktop() ? 36 : 0;

  const isSufficientWidth = windowDimensions.width >= MIN_PIP_WIDTH + 60;
  const isFlexColHeight = windowDimensions.height >= MIN_PIP_HEIGHT * 3 + DESKTOP_HEADER_BUFFER;
  const isSufficientModalHeight = windowDimensions.height >= 200 + DESKTOP_HEADER_BUFFER;
  const isSufficientModalHeightIncoming = windowDimensions.height > 220 + DESKTOP_HEADER_BUFFER;

  if (callStatus === 'idle' && !showDeclinedState) {
    return null;
  }

  if ((isRinging && isIncoming) || showDeclinedState) {
    return (
      <div
        ref={rootRef}
        className={cn('flex flex-1 flex-col items-center justify-center text-primary', className)}
      >
        <div
          className={cn(
            'flex w-full gap-1 items-center justify-between',
            isFlexColHeight ? 'flex-col flex-1' : 'flex-row px-3',
            isDesktop() ? 'pb-3' : 'py-3',
          )}
        >
          {isFlexColHeight && (
            <div
              className={cn(
                'flex w-full flex-col gap-0 border-b border-primary-5 px-3 pb-2 transition-opacity duration-1000',
                showDeclinedState && 'opacity-50',
              )}
            >
              <div className="flex w-full items-center justify-center gap-1">
                <CountryFlag countryCode={userLineCountry} size="l" />

                <div className="flex min-w-0 flex-1 items-center gap-1">
                  <AutoSizeText>{userLine?.lineName}</AutoSizeText>
                </div>

                {!isDesktop() && onDismiss && (
                  <button
                    type="button"
                    className="items-center justify-center h-7 w-7"
                    onClick={() => {
                      onDismiss?.();
                    }}
                  >
                    <Icon name={Icons.Cancel} className="text-primary ml-auto cursor-pointer" />
                  </button>
                )}
              </div>

              {extra?.summary && (
                <div className="flex w-full items-start gap-0.5">
                  <Icon name={Icons.ArrowBendDownRight} className="text-primary-70 leading-none" />(
                  <span className="text-primary-70 text-body-13-bold leading-4">
                    {extra.summary}
                  </span>
                  )
                </div>
              )}
            </div>
          )}

          <div
            className={cn(
              'group/avatar flex gap-0 flex-1 items-center transition-opacity duration-1000',
              isSufficientModalHeightIncoming
                ? 'flex-col justify-center w-full'
                : 'flex-row w-full',
              isFlexColHeight && 'px-3',
              showDeclinedState && 'opacity-50',
            )}
          >
            <ContactAvatar
              name={contact?.name || ''}
              size={isSufficientModalHeightIncoming ? 'lg' : 'm'}
              shape="square"
              className={cn('cursor-pointer avatar-trigger', isFlexColHeight ? 'pb-2' : 'pr-2')}
              style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
              onClick={handleContactClick}
            />

            <div
              className={cn(
                'flex min-w-0 flex-col overflow-hidden w-full select-none gap-0.5',
                isSufficientModalHeightIncoming && 'items-center',
              )}
              style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
            >
              <button
                type="button"
                className="flex flex-col w-fit cursor-pointer"
                onClick={handleContactClick}
              >
                <AutoSizeText className={cn(isSufficientModalHeightIncoming && 'peer text-center')}>
                  {contact?.name && contact?.name !== contact?.phoneNumber
                    ? contact?.name
                    : formatPhoneNumberForDisplay(contact?.phoneNumber, userLineCountry)}
                </AutoSizeText>

                <span className="h-px w-full bg-primary-10 opacity-0 peer-hover:opacity-100 group-has-[.avatar-trigger:hover]/avatar:opacity-100" />
              </button>

              {/* NOTE: name equals phonenumber before whisper response; whisper response will update name accordingly to undefined or name if exists */}
              <ContactSubline
                contact={contact}
                lineCountry={userLineCountry}
                centered={isSufficientModalHeightIncoming}
                paused={showDeclinedState}
              />
            </div>
          </div>

          <div
            className={cn(
              'flex flex-row gap-1 items-center px-3 w-full',
              isFlexColHeight && 'justify-between',
              showDeclinedState ? 'gap-0' : 'gap-1',
            )}
            style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
          >
            <Tooltip
              delayDuration={0}
              disableHoverableContent
              open={showDeclinedState ? false : undefined}
            >
              <TooltipTrigger asChild>
                <button
                  className={cn(
                    'relative flex flex-1 items-center justify-center gap-1 h-10 rounded-xl border px-3 py-2.5 shadow-xs transition-all duration-1000 select-none overflow-hidden min-w-12',
                    showDeclinedState
                      ? 'border-transparent bg-primary-10'
                      : 'border-white/30 bg-extra-decline disabled:opacity-50 enabled:hover:bg-status-danger cursor-pointer',
                    !showDeclinedState && (isSufficientWidth || isFlexColHeight) && 'w-[90px]',
                  )}
                  disabled={isProcessing}
                  onClick={
                    !showDeclinedState
                      ? () => {
                          analytics.track(ANALYTICS_EVENTS.CALL_PIP.DECLINED);
                          handleDecline();
                        }
                      : undefined
                  }
                  type="button"
                >
                  <span
                    className={cn(
                      'absolute flex items-center gap-1 transition-opacity duration-1000',
                      showDeclinedState ? 'opacity-0' : 'opacity-100',
                    )}
                  >
                    <Icon name={Icons.PhoneFill} className="text-white rotate-135" />
                    {(isSufficientWidth || isFlexColHeight) && (
                      <P3 className="text-white">{t('components.incomingCall.decline')}</P3>
                    )}
                  </span>
                  <span
                    className={cn(
                      'absolute text-body-14-bold text-primary transition-opacity duration-1000',
                      showDeclinedState ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    {renderDeclineState()}
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" container={pipWindow?.document.body}>
                {declineTooltipText()}
              </TooltipContent>
            </Tooltip>

            <button
              className={cn(
                'flex items-center justify-center gap-1 h-10 rounded-xl border border-white/30 py-2.5 shadow-xs cursor-pointer bg-extra-answer disabled:cursor-default disabled:opacity-50 enabled:hover:bg-[#2CC654] transition-all duration-1000 select-none overflow-hidden max-w-1/2',
                showDeclinedState
                  ? 'w-0 min-w-0 px-0 opacity-0 border-0'
                  : cn(
                      'flex-1 px-3 min-w-12',
                      (isSufficientWidth || isFlexColHeight) && 'w-[90px]',
                    ),
              )}
              type="button"
              disabled={isProcessing}
              onClick={
                !showDeclinedState
                  ? () => {
                      analytics.track(ANALYTICS_EVENTS.CALL_PIP.ACCEPTED);
                      handleAnswer();
                    }
                  : undefined
              }
            >
              {isProcessing && !showDeclinedState ? (
                <LoadingSpinner className="border-white/40 border-t-white" />
              ) : (
                <>
                  <Icon name={Icons.PhoneFill} className="text-white" />
                  {(isSufficientWidth || isFlexColHeight) && (
                    <P3 className="text-white">{t('components.incomingCall.accept')}</P3>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={cn(
        'relative flex flex-1 flex-col items-center justify-center py-3 text-primary',
        className,
      )}
    >
      <div
        ref={setDropdownContainer}
        style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
      />
      <div className="h-1/3 w-full bg-linear-to-l from-[#00AA69]/40 from-[10.41%] via-[#6DCF00]/40 via-50% to-[#FFDC00]/40 to-[90%] absolute -top-10 blur-3xl opacity-50 -z-1" />

      <div
        className={cn(
          'flex w-full gap-1 items-center justify-between',
          isFlexColHeight ? 'flex-col flex-1' : 'flex-row px-3 gap-2',
        )}
      >
        <div
          className={cn(
            'group/avatar flex gap-0 flex-1 items-center w-full',
            isSufficientModalHeight ? 'flex-col justify-center' : 'flex-row',
            isFlexColHeight && 'px-3',
            isCallTagsOpen && 'justify-start',
          )}
        >
          <ContactAvatar
            name={contact?.name || ''}
            size={isSufficientModalHeight ? 'lg' : 'm'}
            shape="square"
            className={cn('cursor-pointer avatar-trigger', isFlexColHeight ? 'pb-2' : 'pr-2')}
            style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
            onClick={handleContactClick}
          />

          <div
            className={cn(
              'flex gap-2 items-center w-full',
              isSufficientModalHeight ? 'flex-col' : 'flex-row flex-1',
            )}
          >
            {(isSufficientWidth || isFlexColHeight) && (
              <div
                className={cn(
                  'flex flex-col w-full select-none',
                  isSufficientModalHeight && 'items-center',
                )}
                style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
              >
                <button
                  type="button"
                  className="flex flex-col w-fit cursor-pointer"
                  onClick={handleContactClick}
                >
                  <span
                    className={cn(
                      'text-body-14 text-primary break-all line-clamp-1 peer',
                      !isSufficientModalHeight && 'text-left',
                    )}
                  >
                    {contact?.name ??
                      formatPhoneNumberForDisplay(contact?.phoneNumber, userLineCountry)}
                  </span>

                  <span className="h-px w-full bg-primary-10 opacity-0 peer-hover:opacity-100 group-has-[.avatar-trigger:hover]/avatar:opacity-100" />
                </button>

                <ContactSubline
                  className="pointer-events-none"
                  contact={contact}
                  lineCountry={userLineCountry}
                  centered={isSufficientModalHeight}
                />
              </div>
            )}

            {isOnline && !isPoorQuality ? (
              <div
                className={cn(
                  'flex shrink-0 items-center gap-1.5 text-primary-60',
                  !isSufficientModalHeight && isSufficientWidth && 'ml-auto',
                )}
              >
                <NetworkStatusIndicator />
                {renderCallStatus()}
              </div>
            ) : (
              <NetworkStatusIndicator
                className={cn(
                  'border-none',
                  !isSufficientModalHeight && isSufficientWidth && 'ml-auto',
                  !isSufficientModalHeight && 'pl-0 pr-0',
                )}
                showText={isSufficientModalHeight}
              />
            )}
          </div>
        </div>

        <div
          className={cn('flex flex-row gap-1 items-center', isFlexColHeight && 'justify-between')}
          style={{ WebkitAppRegion: 'no-drag' } as React.CSSProperties}
        >
          <div className="flex flex-row gap-1">
            <div className="flex h-8 items-center rounded-xl border-t border-b border-r border-primary-10 bg-primary-3 shadow-md">
              <button
                type="button"
                className={cn(
                  'transition-colors duration-100 cursor-pointer',
                  'flex size-8 items-center justify-center rounded-xl border px-2',
                  isMuted ? 'border-primary-70 bg-primary' : 'border-primary-10 bg-white',
                )}
                onClick={() => {
                  analytics.track(
                    isMuted ? ANALYTICS_EVENTS.CALL_PIP.UNMUTED : ANALYTICS_EVENTS.CALL_PIP.MUTED,
                  );
                  onToggleMute();
                }}
              >
                <Icon
                  name={isMuted ? Icons.MicrophoneSlash : Icons.Microphone}
                  className={cn('text-body-18', isMuted ? 'text-white' : 'text-primary')}
                />
              </button>

              <button
                type="button"
                className={cn(
                  'flex h-9 w-8 items-center justify-center gap-0.5 rounded-xl focus:outline-none focus-visible:outline-none cursor-pointer',
                )}
                onClick={handleOpenParentWindow}
              >
                {callStatus === 'in-progress' && !isMuted ? (
                  <div className="size-4">
                    <LiveWaveform
                      active={true}
                      deviceId={microphoneDeviceId || 'default'}
                      height={16}
                      mode="static"
                      barWidth={2}
                      barGap={2}
                      barRadius={4}
                      barHeight={4}
                      barColor="rgb(var(--primary-80))"
                      fadeEdges={false}
                      sensitivity={1.5}
                      className="w-3"
                      onStreamReady={handleWaveformStreamReady}
                      onStreamEnd={handleWaveformStreamEnd}
                    />
                  </div>
                ) : (
                  <div className="flex size-4 items-center justify-center gap-0.5 pr-1.5">
                    <div className="h-1 w-0.5 rounded-xs bg-primary-80" />
                    <div className="h-1 w-0.5 rounded-xs bg-primary-80" />
                    <div className="h-1 w-0.5 rounded-xs bg-primary-80" />
                  </div>
                )}
              </button>
            </div>
          </div>

          {callTags && callStatus === 'in-progress' && (
            <CallTagsDropdown
              tags={callTags.tags}
              selectedTagKeys={callTags.selectedTagKeys}
              onAddTag={callTags.onAddTag}
              onRemoveTag={callTags.onRemoveTag}
              container={dropdownContainer}
              open={isCallTagsOpen}
              onOpenChange={handleCallTagsOpenChange}
            />
          )}

          <button
            className={cn(
              'flex items-center justify-center gap-1 min-w-12 h-[34px] rounded-xl border border-white/30 px-3 py-2.5 shadow-xs cursor-pointer bg-extra-decline disabled:opacity-50 enabled:hover:bg-status-danger',
              (isSufficientWidth || isFlexColHeight) && 'w-[90px]',
            )}
            type="button"
            disabled={isHangingUp}
            onClick={() => {
              analytics.track(ANALYTICS_EVENTS.CALL_PIP.HUNG_UP);
              handleHangup();
            }}
          >
            <Icon name={Icons.PhoneFill} className="text-white rotate-135" />
          </button>
        </div>
      </div>
    </div>
  );
}
